import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertCircle,
  Building2,
  CheckCircle,
  Eye,
  EyeOff,
  Lock,
  Mail,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { toUserFacingError } from "../../utils/userFacingError";

const EmployerLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login, isLoading, error, requires2FA } = useAuth();
  const displayError = error
    ? toUserFacingError(error, "Unable to sign in right now. Please try again.")
    : null;
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    rememberMe: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isSubmitting || isLoading) {
      return;
    }

    setIsSubmitting(true);
    try {
      const slug = await login(
        formData.email,
        formData.password,
        undefined,
        formData.rememberMe
      );
      if (!slug) {
        return;
      }
      if (requires2FA) {
        navigate(`/client/${slug}/admin/2fa-setup`);
      } else {
        navigate(`/client/${slug}/admin/dashboard`);
      }
    } catch (err) {
      console.error("Login error:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <div
        className="hidden lg:flex lg:w-1/2 relative"
        style={{ backgroundColor: "#0076b5" }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900 to-blue-700 opacity-90" />
        <div className="relative z-10 flex flex-col justify-center px-12 text-white">
          <div className="mb-8">
            <div className="w-16 h-16 bg-white bg-opacity-20 rounded-lg flex items-center justify-center mb-4">
              <Building2 className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold mb-2">VPD Employer Portal</h1>
            <p className="text-blue-100">
              Sign in with your employer account. Your company workspace opens
              after authentication.
            </p>
          </div>
          <div className="space-y-6">
            <div className="flex items-center space-x-3">
              <CheckCircle className="h-5 w-5 text-green-300" />
              <span className="text-blue-100">Secure employer access</span>
            </div>
            <div className="flex items-center space-x-3">
              <CheckCircle className="h-5 w-5 text-green-300" />
              <span className="text-blue-100">
                Company slug applied after login
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Sign in</h2>
            <p className="mt-2 text-sm text-gray-600">
              Access the VPD Employer Portal
            </p>
          </div>

          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            {displayError && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                  <p className="ml-3 text-sm text-red-800">{displayError}</p>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700"
                >
                  Work Email
                </label>
                <div className="mt-1 relative">
                  <Mail className="h-5 w-5 text-gray-400 absolute left-3 top-3.5" />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={formData.email}
                    onChange={(event) =>
                      setFormData((prev) => ({
                        ...prev,
                        email: event.target.value,
                      }))
                    }
                    className="appearance-none block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter your work email"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700"
                >
                  Password
                </label>
                <div className="mt-1 relative">
                  <Lock className="h-5 w-5 text-gray-400 absolute left-3 top-3.5" />
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    required
                    value={formData.password}
                    onChange={(event) =>
                      setFormData((prev) => ({
                        ...prev,
                        password: event.target.value,
                      }))
                    }
                    className="appearance-none block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword((prev) => !prev)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center text-sm text-gray-900">
                <input
                  type="checkbox"
                  checked={formData.rememberMe}
                  onChange={(event) =>
                    setFormData((prev) => ({
                      ...prev,
                      rememberMe: event.target.checked,
                    }))
                  }
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded mr-2"
                />
                Remember me for 30 days
              </label>
            </div>

            <button
              type="submit"
              disabled={isLoading || isSubmitting}
              className="w-full flex justify-center py-3 px-4 rounded-lg text-sm font-medium text-white disabled:opacity-50"
              style={{ backgroundColor: "#0076b5" }}
            >
              {isLoading || isSubmitting ? "Signing in..." : "Sign in"}
            </button>

            <div className="text-center space-y-2">
              <p className="text-sm text-gray-600">
                New employer?{" "}
                <Link
                  to="/onboard"
                  className="font-medium text-blue-600 hover:text-blue-500"
                >
                  Register your business
                </Link>
              </p>
              <p className="text-sm text-gray-600">
                Need help? Contact{" "}
                <a
                  href="mailto:support@vpdmoney.net"
                  className="font-medium text-blue-600 hover:text-blue-500"
                >
                  support@vpdmoney.net
                </a>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EmployerLoginPage;
